import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import "./Manufacture.css";

const STATUS_META = {
  returned_mid_transit: "Returned Mid-Transit",
  qa_check: "QA Check",
  to_clean: "To Clean",
  cleaned: "Cleaned",
  repair_needed: "Repair Needed",
  in_repair: "In Repair",
  repaired: "Repaired",
  repack_pending: "Repack Pending",
  repacked: "Repacked",
  ready_for_storage: "Ready for Storage",
  total_loss: "Total Loss",
};

export default function ManufactureHistory() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    employee: "all",
    fromDate: "",
    toDate: "",
  });

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await client.get("/manufacture/history/updates");
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Manufacture history load failed:", error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const employees = useMemo(() => {
    const names = history.map((row) => row.updated_by || row.user).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [history]);

  const filteredHistory = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return history.filter((row) => {
      const rowDate = row.created_at || row.at ? new Date(row.created_at || row.at) : null;
      if (filters.employee !== "all" && (row.updated_by || row.user || "") !== filters.employee) return false;
      if (filters.fromDate && (!rowDate || rowDate < new Date(filters.fromDate))) return false;
      if (filters.toDate && (!rowDate || rowDate > new Date(`${filters.toDate}T23:59:59`))) return false;
      if (search) {
        const haystack = [
          row.action,
          row.orderid,
          row.product,
          row.updated_by,
          row.user,
          row.source_location,
          row.destination_location,
          STATUS_META[row.from_status || row.fromStatus],
          STATUS_META[row.to_status || row.toStatus],
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [history, filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ search: "", employee: "all", fromDate: "", toDate: "" });
  };

  return (
    <div className="manufacturing-container manufacture-history-page page-container">
      <div className="page-head">
        <h2>Manufacture History</h2>
        <div className="manufacture-top-actions">
          <button className="btn outline" onClick={() => navigate("/manufacture")}>
            Back
          </button>
          <button className="btn primary" onClick={fetchHistory} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="manufacture-filters-card">
        <div className="manufacture-history-filters-grid">
          <input
            className="filter-input"
            placeholder="Search order, product, action, status"
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
          />
          <select
            className="filter-input"
            value={filters.employee}
            onChange={(e) => updateFilter("employee", e.target.value)}
          >
            <option value="all">All Employees</option>
            {employees.map((employee) => (
              <option key={employee} value={employee}>
                {employee}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="filter-input"
            value={filters.fromDate}
            onChange={(e) => updateFilter("fromDate", e.target.value)}
          />
          <input
            type="date"
            className="filter-input"
            value={filters.toDate}
            onChange={(e) => updateFilter("toDate", e.target.value)}
          />
          <button className="btn outline" onClick={resetFilters}>
            Reset Filters
          </button>
        </div>
      </div>

      <div className="table-card manufacture-history-full-card">
        <table className="manufacturing-table manufacture-history-table">
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>Employee</th>
              <th>Action</th>
              <th>Order</th>
              <th>Product</th>
              <th>Stock</th>
              <th>Moved Qty</th>
              <th>From</th>
              <th>To</th>
              <th>Source</th>
              <th>Destination</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" style={{ textAlign: "center", padding: 18 }}>
                  Loading...
                </td>
              </tr>
            ) : filteredHistory.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: "center", padding: 18 }}>
                  No history found
                </td>
              </tr>
            ) : (
              filteredHistory.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.created_at || row.at
                      ? new Date(row.created_at || row.at).toLocaleString("en-IN")
                      : "-"}
                  </td>
                  <td>{row.updated_by || row.user || "-"}</td>
                  <td>{row.action || "-"}</td>
                  <td>{row.orderid || "-"}</td>
                  <td>{row.product || "-"}</td>
                  <td>{row.qty || 0}</td>
                  <td>{row.moved_qty || row.movedQty || "-"}</td>
                  <td>{STATUS_META[row.from_status || row.fromStatus] || row.source_location || "-"}</td>
                  <td>{STATUS_META[row.to_status || row.toStatus] || row.destination_location || "-"}</td>
                  <td>{row.source_location || "-"}</td>
                  <td>{row.destination_location || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
